class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.carouselId = this.getAttribute("data-carousel-id");
    this.carousel = this.querySelector(`#carousel-${this.carouselId}`);
    this.carouselItems = this.querySelectorAll(
      `#carousel-${this.carouselId} [id^="carousel-item-${this.carouselId}-"]`
    );
    this.prevButtons = this.querySelectorAll(
      `.carousel-previous-button[aria-controls="carousel-${this.carouselId}"]`
    );
    this.nextButtons = this.querySelectorAll(
      `.carousel-next-button[aria-controls="carousel-${this.carouselId}"]`
    );
    this.directionalButtonContainers = this.querySelectorAll(
      `.carousel-directional-button-container-${this.carouselId}`
    );
    this.paginationContainer = this.querySelector(
      `#carousel-pagination-container-${this.carouselId}`
    );
    this.paginationButtonsContainer = this.querySelector(
      `#carousel-pagination-buttons-container-${this.carouselId}`
    );
    this.pageCountsContainer = this.querySelector(
      `#carousel-page-counts-container-${this.carouselId}`
    );
    this.activePageElement = this.querySelector(
      `.carousel-active-page-${this.carouselId}`
    );
    this.totalPagesElement = this.querySelector(
      `.carousel-total-pages-${this.carouselId}`
    );

    this.pageButtons = [];
    this.currentPage = 1;
    this.startX = 0;
    this.isSwiping = false;
    this.swipeThreshold = 125;
    this.distance = 0;

    if (!this.carousel) return;

    window.addEventListener("load", this.initializeCarousel.bind(this));

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        this.initializeCarousel();
        this.goToPage(1);
      }, 150)
    );
    resizeObserver.observe(this.carousel);

    const debounceUpdateCarousel = debounce(
      this.updateCarousel.bind(this),
      150
    );
    this.carousel.addEventListener("scroll", debounceUpdateCarousel);

    if (this.prevButtons.length && this.nextButtons.length) {
      this.prevButtons.forEach((button) =>
        button.addEventListener("click", this.onButtonClick.bind(this))
      );
      this.nextButtons.forEach((button) =>
        button.addEventListener("click", this.onButtonClick.bind(this))
      );
    }

    this.carousel.addEventListener("touchstart", this.onTouchStart.bind(this), {
      passive: false,
    });
    this.carousel.addEventListener("touchmove", this.onTouchMove.bind(this), {
      passive: false,
    });
    this.carousel.addEventListener("touchend", this.onTouchEnd.bind(this));
  }

  initializeCarousel() {
    this.computeItemsPerView();
    this.generatePagination();
  }

  computeItemsPerView() {
    const visibleItems = Array.from(this.carouselItems).filter(
      (item) => item.offsetWidth > 0
    );
    if (visibleItems.length === 0) return;

    const firstItemRect = visibleItems[0].getBoundingClientRect();
    const carouselRect = this.carousel.getBoundingClientRect();
    const itemsPerPage = Math.floor(carouselRect.width / firstItemRect.width);

    this.carouselItemsPerPage = itemsPerPage;
    this.totalPages = Math.ceil(visibleItems.length / itemsPerPage);

    this.updateCarousel();
  }

  updatePageCounts() {
    if (this.activePageElement) {
      this.activePageElement.textContent = this.currentPage;
    }
    if (this.totalPagesElement) {
      this.totalPagesElement.textContent = this.totalPages;
    }
  }

  toggleButtonState(buttons, disable) {
    buttons.forEach((button) => (button.disabled = disable));
  }

  updateButtonStates() {
    if (this.prevButtons.length && this.nextButtons.length) {
      this.toggleButtonState(this.prevButtons, this.currentPage === 1);
      this.toggleButtonState(
        this.nextButtons,
        this.currentPage === this.totalPages
      );
    }
  }

  generatePagination() {
    this.paginationButtonsContainer.innerHTML = "";

    if (this.totalPages > 1) {
      this.paginationContainer.classList.remove("hidden");

      for (let i = 1; i <= this.totalPages; i++) {
        const listItem = this.createPageButton(i);
        this.paginationButtonsContainer.appendChild(listItem);
        this.pageButtons.push(listItem.querySelector("button"));
      }

      this.updateActivePageButton();
    } else {
      this.paginationContainer.classList.add("hidden");
    }
  }

  createPageButton(page) {
    const listItem = document.createElement("li");
    listItem.className = `carousel-page-item-${this.carouselId}`;

    const button = document.createElement("button");
    button.type = "button";
    button.className = `carousel-page-button-${this.carouselId} rounded-full px-2 pb-1 pt-3 ring-1 ring-primary transition-colors duration-300 cursor-pointer`;
    button.setAttribute("data-page", page);
    button.setAttribute("aria-label", `Go to page ${page}`);

    button.addEventListener("click", () => this.goToPage(page));

    listItem.appendChild(button);
    return listItem;
  }

  updateActivePageButton() {
    if (this.pageButtons.length === 0) return;

    this.pageButtons.forEach((button) => {
      if (parseInt(button.dataset.page, 10) === this.currentPage) {
        // Active page button
        button.classList.add("bg-current");
        button.classList.remove("hover:bg-current");
      } else {
        // Non-active page button
        button.classList.remove("bg-current");
        button.classList.add("hover:bg-current");
      }
    });
  }

  updateCarousel() {
    if (!this.carousel) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.carousel.scrollLeft / this.carousel.offsetWidth) + 1;

    if (this.currentPage !== previousPage) {
      this.updateActivePageButton();
    }

    this.updatePageCounts();
    this.updateDirectionalButtons();
    this.updateButtonStates();
  }

  updateDirectionalButtons() {
    if (this.totalPages > 1) {
      if (this.directionalButtonContainers.length) {
        this.directionalButtonContainers.forEach((container) =>
          container.classList.add("lg:block")
        );
      }
      if (this.totalPages > 5) {
        // this.pageCountsContainer.classList.remove("hidden");
        // this.pageCountsContainer.classList.add("flex", "md:hidden");
        this.paginationButtonsContainer.classList.add("hidden", "md:flex");
      }
    } else {
      if (this.directionalButtonContainers.length) {
        this.directionalButtonContainers.forEach((container) =>
          container.classList.remove("lg:block")
        );
      }
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.directionalButtonContainers.length) {
      const direction = event.currentTarget.classList.contains(
        "carousel-next-button"
      )
        ? 1
        : -1;
      this.goToPage(this.currentPage + direction);
    }
  }

  goToPage(page) {
    const newPage = Math.max(1, Math.min(this.totalPages, page));
    const position = (newPage - 1) * this.carousel.offsetWidth;
    this.carousel.scrollTo({ left: position, behavior: "smooth" });
    this.currentPage = newPage;
    this.updateActivePageButton();
  }

  onTouchStart(event) {
    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.isSwiping = true;
    this.distanceX = 0;
    this.distanceY = 0;
  }

  onTouchMove(event) {
    if (!this.isSwiping) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;

    this.distanceX = currentX - this.startX;
    this.distanceY = currentY - this.startY;

    if (Math.abs(this.distanceX) > Math.abs(this.distanceY)) {
      if (event.cancelable) {
        event.preventDefault();
      }
    }
  }

  onTouchEnd() {
    if (!this.isSwiping) return;

    if (Math.abs(this.distanceX) > Math.abs(this.distanceY)) {
      if (Math.abs(this.distanceX) > this.swipeThreshold) {
        const direction = this.distanceX > 0 ? -1 : 1;
        this.goToPage(this.currentPage + direction);
      }
    }

    this.isSwiping = false;
    this.distanceX = 0;
    this.distanceY = 0;
  }
}

customElements.define("carousel-component", CarouselComponent);
